import { Download, FileText, Archive, Code, File } from "lucide-react"
import { Button } from "../ui/button"
import Card, { CardContent, CardHeader, CardTitle } from "../Card"

export default function DownloadBlock({ block, hideTitle = false }) {
  const { content } = block
  const files = Array.isArray(content?.files) ? content.files : []
  const instructions = typeof content?.instructions === 'string'
    ? content.instructions
    : (content?.instructions?.content || '')

  // Safety check for content structure
  if (!content || !Array.isArray(content.files)) {
    return (
      <div className="space-y-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <p className="text-red-600">Erreur: Structure de données invalide pour le bloc de téléchargement</p>
            <pre className="text-xs mt-2 text-left bg-white p-2 rounded">
              {JSON.stringify(content, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Get appropriate icon based on file type
  const getFileIcon = (type) => {
    const lowerType = String(type || '').toLowerCase()
    if (lowerType.includes('javascript') || lowerType.includes('js') || lowerType.includes('jsx')) {
      return <Code className="w-5 h-5 text-yellow-600" />
    }
    if (lowerType.includes('archive') || lowerType.includes('zip') || lowerType.includes('rar')) {
      return <Archive className="w-5 h-5 text-purple-600" />
    }
    if (lowerType.includes('pdf') || lowerType.includes('document')) {
      return <FileText className="w-5 h-5 text-red-600" />
    }
    return <File className="w-5 h-5 text-gray-600" />
  }

  // Get file type color
  const getFileTypeColor = (type) => {
    const lowerType = String(type || '').toLowerCase()
    if (lowerType.includes('javascript') || lowerType.includes('js') || lowerType.includes('jsx')) {
      return 'bg-yellow-100 text-yellow-800'
    }
    if (lowerType.includes('archive') || lowerType.includes('zip') || lowerType.includes('rar')) {
      return 'bg-purple-100 text-purple-800'
    }
    if (lowerType.includes('pdf') || lowerType.includes('document')) {
      return 'bg-red-100 text-red-800'
    }
    return 'bg-gray-100 text-gray-800'
  }

  // Format file size for better readability
  const formatFileSize = (size) => {
    return size || 'Taille inconnue'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {!hideTitle && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-6 h-6 text-blue-600" />
              {block.title}
            </CardTitle>
            {block.description && (
              <p className="text-gray-600">{block.description}</p>
            )}
          </CardHeader>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Instructions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-gray-700 leading-relaxed">
              {instructions || 'Aucune instruction particulière.'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Files List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Fichiers à télécharger ({files.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {files.map((file, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* File Icon */}
                    <div className="flex-shrink-0">
                      {getFileIcon(file.type)}
                    </div>
                    
                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{file.name || 'Document téléchargeable'}</h4>
                      <div className="flex items-center gap-3 mt-1">
                        {file.type && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getFileTypeColor(file.type)}`}>
                            {file.type}
                          </span>
                        )}
                        <span className="text-sm text-gray-500">
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Download Button */}
                  <div className="flex-shrink-0 ml-4">
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 inline-flex items-center justify-center gap-2 h-10 leading-none"
                      asChild
                    >
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="inline-flex items-center justify-center gap-2 h-10 leading-none"
                      >
                        <Download className="w-4 h-4" />
                        <span>Télécharger</span>
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Download All Button */}
          {files.length > 1 && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-3">
                  Téléchargez tous les fichiers individuellement ou utilisez un gestionnaire de téléchargement pour les récupérer en lot.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {files.map((file, index) => (
                    <Button key={index} size="sm" variant="outline" asChild>
                      <a href={file.url} target="_blank" rel="noopener noreferrer" download>
                        {file.name || `Fichier ${index + 1}`}
                      </a>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <FileText className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium text-green-800 mb-1">Conseils de téléchargement</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Vérifiez que vous avez suffisamment d'espace de stockage</li>
                <li>• Les fichiers peuvent être utilisés hors ligne après téléchargement</li>
                <li>• Conservez les fichiers dans un dossier dédié au cours</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
